import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import type { History } from 'history';
import { getPageRoute } from '~/utils/client-experience.utils';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import { CXPage } from '~/components/ClientExperience/OneFlow/models/cx-page.model';
import TokenExpiredError from '~/services/TokenExpiredError';
import {
    IFlowTransformedData,
    IRecurringInvoiceResponse,
    IRecurringScheduleBlockResponse,
    IVariableResponse
} from '~/types/server.types';
import {
    ErrorType,
    ForceStrongAuthReason,
    IsWeakAuthenticatingReason
} from '~/components/ClientExperience/OneFlow/stores/flow-view.ui.store';
import { redirectToUrl } from '~/utils/url.utils';
import { LogEvents } from '~/components/ClientExperience/services/logger/log-events';
import { TFlowStatus } from '~/types/model.types';
import { CXFlow } from '~/components/ClientExperience/OneFlow/models/cx-flow.model';
import {
    PENDING_INVOICE_FIELD_NAME,
    PENDING_SELECTION_FIELD_NAME,
    SIGNATURES_BLOCK_FIELD_NAME
} from '~/components/ClientExperience/OneFlow/components/FlowView/FlowForm.utils';
import { trackError } from '~/services/logger/error.utils';
import { isEmpty, isEqual } from 'lodash';
import useAnalytics from '~/common/useAnalytics';
import { transformFlowResponse } from '~/utils/server-response.utils';
import { forceReload } from '~/utils/dom.utils';
import { getForterToken } from '~/utils/forter-load.utils';
import { TSmartFields } from '~/common/SmartFields/smart-fields.types';
import { raceFunction } from '~/utils/data.utils';
import { RequireAtLeastOne } from '@honeybook/hbui/dist/tokens/types';
import { Flow } from '~/models/flow.model';
import {
    CaptureThumbnailParams,
    DEFAULT_CAPTURE_THUMBNAIL_PARAMS
} from '~/components/ClientExperience/components/PublicTemplateThumbnails/types';
import { arrayBufferToBase64 } from '~/components/ClientExperience/components/PublicTemplateThumbnails/utils';
import { IAuthResponse } from '~/components/ClientExperience/types/server.types';
import { JSTORAGE_KEY } from '~/services/j-storage.service';
import { REACT_STORAGE_AUTH_KEY } from '~/common/storage.constants';

interface IHandleFlowErrorProps {
    error: any;
    callback?: Function;
}

interface IVariableStatusResponse {
    variables?: IVariableResponse[];
    new_status?: TFlowStatus;
    submitted_on?: string;
    smart_fields?: TSmartFields;
}

export class FlowCXController {
    rootStore: CXRootStore;

    constructor(rootStore: CXRootStore) {
        this.rootStore = rootStore;
    }

    async userValidateWeakAuth({
        flowId,
        reason,
        onForceAuth
    }: {
        flowId: string;
        reason: ForceStrongAuthReason;
        onForceAuth?: () => void;
    }): Promise<Partial<IAuthResponse> | null> {
        const { flowService, flowSessionStore, flowStore } = this.rootStore;

        if (
            !flowStore.minimalFlow ||
            !flowSessionStore.weakAuthenticated ||
            (flowStore.minimalFlow.require_authentication &&
                !flowStore.minimalFlow?.improved_smaf_auth_on)
        ) {
            return;
        }

        let userAuth = null;

        try {
            const fToken = getForterToken();
            const reasonForWeakAuth =
                reason ===
                ForceStrongAuthReason.clientExperienceMemberAccessFromEmail
                    ? IsWeakAuthenticatingReason.enterEmailAuthCheck
                    : IsWeakAuthenticatingReason.initialAuthCheck;
            flowSessionStore.setWeakAuthenticating(reasonForWeakAuth);
            userAuth = await flowService.checkFlowAuth(flowId, fToken);
            flowSessionStore.setWeakAuthenticating(null);
        } catch (error) {
            this.handleFlowError({ error });
            flowSessionStore.setWeakAuthenticating(null);

            return null;
        }

        if (!userAuth.force_auth) {
            return userAuth;
        }

        flowSessionStore.setForceStrongAuth(reason);

        if (onForceAuth) {
            onForceAuth();
        }

        return userAuth;
    }

    async fetchFlowMinimalData(flowId: string, userId: string) {
        const { flowService, flowStore } = this.rootStore;

        const flowMinimalData = await flowService.fetchFlowMinimalData(
            flowId,
            userId
        );

        if (flowMinimalData) {
            flowStore.loadMinimal(flowMinimalData);
        }
    }

    async fetchFlow(flowId: string, update = true, isPreview = false) {
        const {
            flowService,
            companyStore,
            flowViewUiStore,
            userStore: { currUser }
        } = this.rootStore;

        const response = await flowService.fetchFlow(
            flowId,
            companyStore.currCompany?.default_timezone
        );

        const vendorAsClient =
            !!currUser &&
            !isPreview &&
            response.flow.members.some(
                member =>
                    member.system_user_type === 'vendor' &&
                    member.company._id == response.flow.company_id &&
                    member._id === currUser._id
            );

        if (
            vendorAsClient &&
            response.flow.changes_pending_approval?.some(ch => !ch.accepted)
        ) {
            flowViewUiStore.setError(ErrorType.clientNeedsToApproveChanges);
            return response;
        }

        if (update) {
            this.updateFlow(
                response.flow,
                response.pages,
                response.blocks,
                response.variables
            );
        }

        return response;
    }

    async fetchFlowForPreview({
        flowId,
        flow,
        shouldUpdate,
        forceFetch
    }: RequireAtLeastOne<
        {
            flowId?: string;
            flow?: Flow;
            shouldUpdate?: boolean;
            forceFetch?: boolean;
        },
        'flowId' | 'flow'
    >) {
        // If we force fetch flow can be null or not but flowId or flow._id must be present.
        if (forceFetch || !flow) {
            return this.fetchFlow(flowId || flow._id, shouldUpdate, true);
        }

        const transformedFlowResponse = transformFlowResponse(
            flow.convertToFlowResponse(),
            this.rootStore.companyStore.currCompany.default_timezone
        );

        if (shouldUpdate) {
            this.updateFlow(
                transformedFlowResponse.flow,
                transformedFlowResponse.pages,
                transformedFlowResponse.blocks,
                transformedFlowResponse.variables
            );
        }

        return transformedFlowResponse;
    }

    async fetchActiveLeadForm(flowId: string) {
        const {
            flowService,
            companyStore: { currCompany }
        } = this.rootStore;

        const response = await flowService.fetchActivePublicFlow(
            flowId,
            currCompany._id
        );

        const transformed = transformFlowResponse(
            response,
            currCompany.default_timezone
        );
        this.updateFlow(
            transformed.flow,
            transformed.pages,
            transformed.blocks,
            transformed.variables
        );

        return transformed;
    }

    async submitFlow(
        flowId: string,
        revisionId: string,
        pageId: string,
        data: any,
        history: History<{}>
    ) {
        const {
            toastStore,
            flowService,
            flowSessionStore: { setForceStrongAuth },
            flowViewUiStore: {
                selectedFlow,
                setShouldShowOutdatedFlowDialog,
                setIsFormSubmitting
            },
            fullstoryService
        } = this.rootStore;

        setIsFormSubmitting(true);

        // try/catch wasn't cathing the error for some reason 🤷‍♂️
        return flowService
            .submitFlow(flowId, revisionId, pageId, data)
            .then((response: IVariableStatusResponse) => {
                selectedFlow.updateVariables(response.variables);
                selectedFlow.setFlowStatus(response.new_status);
                selectedFlow.setSubmittedOn(response.submitted_on);
                if (
                    response?.smart_fields &&
                    !isEqual(selectedFlow.smart_fields, response.smart_fields)
                ) {
                    selectedFlow.setSmartFields(response.smart_fields);
                }
                this.onAfterSubmit(pageId, history);
            })
            .catch(error => {
                const errorType = error?.data?.error_type;

                fullstoryService.error(LogEvents.SubmitFlowError(errorType));

                if (errorType === 'HBFlowIsExpiredError') {
                    toastStore.add({
                        type: ToastType.Error,
                        title: 'Something went wrong',
                        text: `${
                            selectedFlow.title
                        } has expired and cannot be submitted.${
                            selectedFlow?.vendor?.company?.company_name &&
                            ` Please contact ${selectedFlow.vendor.company.company_name} for more details.`
                        }`,
                        autoDismissMs: 12000
                    });

                    return Promise.resolve();
                }

                if (
                    errorType === 'HBWrongFlowRevisionError' ||
                    errorType === 'HBWrongAPIVersionError'
                ) {
                    setShouldShowOutdatedFlowDialog(true);

                    return Promise.resolve();
                }

                if (errorType === 'HBUnauthorizedError') {
                    setForceStrongAuth(ForceStrongAuthReason.vendorAuthRaise);

                    return Promise.resolve();
                }

                return Promise.reject(error);
            })
            .finally(() => {
                setIsFormSubmitting(false);
            });
    }

    async submitViewOnly(flowId: string, pageId: string) {
        const {
            toastStore,
            flowService,
            flowViewUiStore: { setIsFormSubmitting }
        } = this.rootStore;

        setIsFormSubmitting(true);

        return flowService
            .submitViewOnly(flowId, pageId)
            .catch(() => {
                toastStore.add({
                    type: ToastType.Error,
                    title: 'Something went wrong',
                    text: '',
                    autoDismissMs: 12000
                });

                return Promise.resolve();
            })
            .finally(() => {
                setIsFormSubmitting(false);
            });
    }

    async approvePendingChange(flowId: string, blockId: string) {
        const {
            flowService,
            flowStore,
            flowViewUiStore: { selectedFlow },
            clientPortalConfigStore: { isPreview }
        } = this.rootStore;
        if (!isPreview) {
            const response = await flowService.approvePendingChange(
                flowId,
                blockId
            );

            flowStore.updateFlowProps(selectedFlow, response);
        }
    }

    async sign(flowId: string, revisionId: string, pageId: string, data: any) {
        const {
            flowService,
            flowViewUiStore: { selectedFlow, setIsFormSubmitting }
        } = this.rootStore;

        setIsFormSubmitting(true);

        const formattedData = this.formatValuesBeforeSubmit(data);

        // try/catch wasn't catching the error for some reason 🤷‍♂️
        return flowService
            .sign(flowId, revisionId, pageId, formattedData)
            .then((response: IVariableStatusResponse) => {
                selectedFlow.setFlowStatus(response.new_status);
            })
            .catch(error => {
                this.genericErrorHandlingCX(error, selectedFlow);
            })
            .finally(() => {
                setIsFormSubmitting(false);
            });
    }

    answerQuestion = raceFunction(
        (
            flowId: string,
            revisionId: string,
            pageId: string,
            data: Record<string, any>
        ) => {
            const {
                flowService,
                flowViewUiStore: { selectedFlow }
            } = this.rootStore;

            return {
                try: () =>
                    flowService.answerQuestion(
                        flowId,
                        revisionId,
                        pageId,
                        this.formatValuesBeforeSubmit(data)
                    ),
                catch: error => {
                    this.genericErrorHandlingCX(error, selectedFlow);
                },
                debounce: 500
            };
        }
    );

    async selectService(
        flowId: string,
        revisionId: string,
        pageId: string,
        data: any
    ) {
        const {
            flowService,
            flowViewUiStore: { selectedFlow, setIsFormSubmitting }
        } = this.rootStore;

        setIsFormSubmitting(true);

        // try/catch wasn't catching the error for some reason 🤷‍♂️
        return flowService
            .selectService(flowId, revisionId, pageId, data)
            .then(response => {
                // @ts-ignore
                selectedFlow.updateVariables(response.variables);
            })
            .catch(error => {
                this.genericErrorHandlingCX(error, selectedFlow);
            })
            .finally(() => {
                setIsFormSubmitting(false);
            });
    }

    updateFlow(flow, pages, blocks, variables) {
        const {
            flowStore,
            companyStore,
            flowViewUiStore,
            clientPortalConfigStore,
            sessionStore
        } = this.rootStore;

        flowStore.load(flow, pages, blocks, variables);

        const staticCompanyObject =
            clientPortalConfigStore?.configuration?.company_data_for_flows;
        // flow.company is only available for vendors (in honeybook.com - not client portal)
        let companyToUpdate = isEmpty(staticCompanyObject)
            ? flow.company
            : staticCompanyObject;

        if (isEmpty(companyToUpdate)) {
            const error = new Error(
                '[CXFlowCompany]: no company object available for active flow'
            );
            const metadata = {
                flow_id: flow._id,
                cp_company_id: clientPortalConfigStore?.clientPortalCompanyId,
                user_id: sessionStore?.currUserId
            };
            trackError({ error, context: metadata });

            // fallback - not good enough, track error should raise monitor on this error.
            companyToUpdate = {
                _id:
                    flow?.company_id ||
                    clientPortalConfigStore?.clientPortalCompanyId
            };
        }

        companyStore.update(companyToUpdate);
        flowViewUiStore.selectFlow(flow._id);
        flowViewUiStore.selectPage(
            flowViewUiStore.selectedPageId ||
                (Object.values(pages) as CXPage[]).sort(
                    (currPage, nextPage) => currPage.order - nextPage.order
                )[0]?.page_id
        );
    }

    gotActiveRevisionUpdates(flowTransformedData: IFlowTransformedData) {
        const { flowStore, flowViewUiStore } = this.rootStore;

        flowStore.mergeActiveRevisionUpdates(flowTransformedData);

        if (!flowViewUiStore.reloadFormikPropsCallback) {
            return;
        }

        flowViewUiStore.reloadFormikPropsCallback();
    }

    handleFlowError({ error, callback }: IHandleFlowErrorProps) {
        trackError({ error });
        if (error.constructor.name === TokenExpiredError.name) {
            return;
        }

        const { flowViewUiStore } = this.rootStore;
        const errorType =
            error?.response?.data?.error_type || error?.data?.error_type;
        if (errorType === ErrorType.noPermission) {
            flowViewUiStore.setError(ErrorType.noPermission);
        } else if (errorType === ErrorType.clientNeedsToApproveChanges) {
            flowViewUiStore.setError(ErrorType.clientNeedsToApproveChanges);
        } else if (errorType === ErrorType.flowNotFound) {
            flowViewUiStore.setError(ErrorType.flowNotFound);
        } else if (errorType === ErrorType.wrongApiVersion) {
            forceReload('wrongApiVersion');
        } else if (errorType === ErrorType.unauthenticated) {
            localStorage.removeItem(JSTORAGE_KEY);
            localStorage.removeItem(REACT_STORAGE_AUTH_KEY);
            window.location.reload();
            return;
        } else {
            trackError({
                error,
                message: 'flow load error',
                context: {
                    tags: ['custom-error:cx-flow-load-error']
                }
            });

            flowViewUiStore.setError(ErrorType.error);
        }

        if (!callback) {
            return;
        }

        callback(error);
    }

    async reloadFlow() {
        const { flowViewUiStore } = this.rootStore;
        const { selectedFlow } = flowViewUiStore;
        flowViewUiStore.setShouldShowOutdatedFlowDialog(false);
        flowViewUiStore.setIsSignModalOpen(false);
        flowViewUiStore.setShouldShowApproveSuccessProtip(false);

        await this.fetchFlow(selectedFlow._id).catch(error =>
            this.handleFlowError({ error })
        );
    }

    async getFlowPaymentsPreview(isActiveRevisionMode: boolean = false) {
        const { flowViewUiStore, flowService, flowStore } = this.rootStore;
        const { selectedFlow } = flowViewUiStore;

        const selectedSsiBlockIds = flowStore
            .selectedServicesForPreview()
            .map(service => service.service_item_block_id);

        const customQuantityData = flowStore.serviceSelectionCustomQuantities();

        const response = await flowService.getFlowPaymentsPreview(
            selectedFlow._id,
            selectedSsiBlockIds,
            customQuantityData,
            isActiveRevisionMode,
            selectedFlow.invoiceSummaryBlock?.numberOfPayments
        );

        flowStore.syncSelectedFlowWithServerResponse(
            null,
            null,
            response.variables
        );
    }

    createPageSchema(pageIndex) {
        if (!this.rootStore.flowStore.flow.pages[pageIndex]) {
            return null;
        }
        const { title } = this.rootStore.flowStore.flow.pages[pageIndex];

        return getPageRoute(pageIndex, title);
    }

    goToPage(pageId: string) {
        const { history, selectedFlowId, findPageIndex, selectPage } =
            this.rootStore.flowViewUiStore;

        const pageIndex = findPageIndex(pageId);
        const pageSchema = this.createPageSchema(pageIndex);
        selectPage(pageId);
        history.push(`/flow/${selectedFlowId}/${pageSchema}`);
    }

    goToSchedulePage() {
        const { scheduleContainerBlock } =
            this.rootStore.flowViewUiStore.selectedFlow;

        this.goToPage(scheduleContainerBlock.page_id);
    }

    onAfterSubmit(pageId: string, history: History<{}>) {
        const {
            flowViewUiStore: { selectedFlow },
            fullstoryService
        } = this.rootStore;

        const { pages } = selectedFlow;

        const submittedPage = pages.find(page => page.page_id === pageId);
        const isLastPage = submittedPage?.isLastPage;

        fullstoryService.debug(
            LogEvents.PageSubmitted(submittedPage?.order, pageId)
        );

        this.cancelSessionIfNeeded(submittedPage);

        if (!isLastPage) {
            this.deleteFlowCachedPageDataInLocalStorage(
                selectedFlow._id,
                pageId
            );
            return;
        }

        fullstoryService.debug(LogEvents.LastPageSubmitted);
        this.redirectAfterSubmit(history);

        this.deleteFlowCachedDataInLocalStorage(selectedFlow._id);
    }

    cancelSessionIfNeeded(submittedPage: CXPage) {
        const {
            flowViewUiStore: { selectedFlow }
        } = this.rootStore;

        if (
            !!submittedPage?.sessionSelectionBlock &&
            !!selectedFlow.scheduleContainerBlock.tempVariableValue
        ) {
            const tempSessionConfigId =
                selectedFlow.scheduleContainerBlock.tempVariableValue
                    .session_config_id;

            if (
                tempSessionConfigId !==
                selectedFlow.scheduleContainerBlock.sessionConfig?._id
            ) {
                selectedFlow.scheduleContainerBlock.setTempVariableValue(null);
            }
        }
    }

    deleteFlowCachedDataInLocalStorage(flowId: string) {
        const flowCachedKey: string = `${flowId}_questions`;
        localStorage.removeItem(flowCachedKey);
    }

    deleteFlowCachedPageDataInLocalStorage(flowId: string, pageId: string) {
        const { flowCachedData, flowCachedKey } =
            this.getFlowCachedDataFromLocalStorage(flowId);
        if (flowCachedData) {
            delete flowCachedData[pageId];
        }
        localStorage.setItem(flowCachedKey, JSON.stringify(flowCachedData));
    }

    getFlowCachedDataFromLocalStorage(flowId: string) {
        const flowCachedKey: string = `${flowId}_questions`;
        const flowCachedDataStr: string =
            localStorage.getItem(flowCachedKey) || '{}';
        const flowCachedData = JSON.parse(flowCachedDataStr);
        return { flowCachedData, flowCachedKey };
    }

    redirectAfterSubmit(history: History<{}>) {
        const {
            flowViewUiStore: { selectedFlow },
            clientPortalConfigStore: { isPreview }
        } = this.rootStore;

        const { configuration } = selectedFlow;

        if (
            !isPreview &&
            configuration.is_redirect_url_on &&
            configuration.redirect_url
        ) {
            redirectToUrl(configuration.redirect_url);

            return;
        }

        history.push(`/flow/${selectedFlow._id}/flow-submitted`);
    }

    async getPaymentConnectedInvoice(
        flowId: string,
        paymentBlockId: string
    ): Promise<IRecurringInvoiceResponse> {
        const { flowService } = this.rootStore;
        const resp = await flowService.getPaymentConnectedInvoice(
            flowId,
            paymentBlockId
        );
        const recurringScheduleBlock = resp.blocks.find(
            block => block.type === 'recurring_schedule_block'
        ) as IRecurringScheduleBlockResponse;
        recurringScheduleBlock.current_payment_block_id = paymentBlockId;
        return resp;
    }

    genericErrorHandlingCX(error: any, flow: CXFlow) {
        const {
            toastStore,
            flowViewUiStore: { setShouldShowOutdatedFlowDialog }
        } = this.rootStore;
        if (error?.data?.error_type === 'HBFlowIsExpiredError') {
            toastStore.add({
                type: ToastType.Error,
                text: `${flow.title} has expired ...${
                    flow?.vendor?.company?.company_name &&
                    ` Please contact ${flow.vendor.company.company_name} for more details.`
                }`,
                autoDismissMs: 12000
            });
        }
        if (
            error?.data?.error_type === 'HBWrongFlowRevisionError' ||
            error?.data?.error_type === 'HBWrongAPIVersionError'
        ) {
            setShouldShowOutdatedFlowDialog(true);
        }
        throw error;
    }

    async onFormSubmit({
        values,
        page_id
    }: {
        executeHBRecaptchaV3?: (action: string) => Promise<string>;
        page_id: string;
        track?: ReturnType<typeof useAnalytics>['track'];
        values: Record<string, any>;
    }) {
        const {
            flowViewUiStore: {
                history,
                selectedFlow: {
                    isFlowCompleted,
                    revision_id: revisionId,
                    submitted_on: submittedOn,
                    pages
                },
                selectedFlowId
            }
        } = this.rootStore;

        if (isFlowCompleted && !!submittedOn) {
            const submittedPage = pages.find(page => page.page_id === page_id);
            const isLastPage = submittedPage?.isLastPage;
            if (isLastPage) {
                this.redirectAfterSubmit(history);
            }
            return;
        }

        const {
            transaction_id,
            [SIGNATURES_BLOCK_FIELD_NAME]: _,
            [PENDING_SELECTION_FIELD_NAME]: __,
            [PENDING_INVOICE_FIELD_NAME]: ___,
            ...valuesForSubmission
        } = values;

        const formattedValuesForSubmission =
            this.formatValuesBeforeSubmit(valuesForSubmission);

        await this.submitFlow(
            selectedFlowId,
            revisionId,
            page_id,
            formattedValuesForSubmission,
            history
        );
    }

    formatValuesBeforeSubmit(values: Record<string, any>) {
        const {
            flowViewUiStore: {
                selectedFlow: { uploadQuestionBlocksVariablesIds }
            }
        } = this.rootStore;

        const updatedValuesForSubmission = { ...values };

        Object.entries(updatedValuesForSubmission).forEach(([key, value]) => {
            const isUploadQuestion =
                uploadQuestionBlocksVariablesIds.includes(key);
            if (isUploadQuestion) {
                updatedValuesForSubmission[key] = {
                    raw_data: value.map(file => ({
                        url: file.url
                    }))
                };
            }
        });

        return updatedValuesForSubmission;
    }

    async onSubmitViewOnly({ page_id }: { page_id: string }) {
        const {
            flowViewUiStore: {
                selectedFlow: { isFlowCompleted, submitted_on: submittedOn },
                selectedFlowId
            }
        } = this.rootStore;

        if (isFlowCompleted && !!submittedOn) {
            return;
        }

        await this.submitViewOnly(selectedFlowId, page_id);
    }

    getPageIndexByPageUrlParam(page: string): number {
        const { selectedFlow } = this.rootStore.flowViewUiStore;
        const pageNumber = +page;
        let pageIndex: number;

        if (
            typeof pageNumber === 'number' &&
            pageNumber <= selectedFlow.pages.length
        ) {
            return pageNumber - 1;
        }

        switch (page) {
            case 'payment':
                pageIndex = selectedFlow.chargeBlockPageIndex;
                break;
            case 'scheduler':
                pageIndex = selectedFlow.scheduleContainerBlockIndex;
                break;
        }

        pageIndex = pageIndex > 0 ? pageIndex : 0;

        return pageIndex;
    }

    async selectNumberOfPayments(numberOfPayments: number) {
        const {
            flowService,
            flowViewUiStore: {
                flowId,
                selectedFlow: { scheduleContainerBlock }
            }
        } = this.rootStore;

        const tempVariable = scheduleContainerBlock?.tempVariableValue;

        const res = await flowService.selectNumberOfPayments({
            flowId,
            numberOfPayments
        });

        this.updateFlow(res.flow, res.pages, res.blocks, res.variables);

        if (tempVariable) {
            this.rootStore.flowViewUiStore.selectedFlow.scheduleContainerBlock.setTempVariableValue(
                tempVariable
            );
        }
    }

    async getPageThumbnail(params: CaptureThumbnailParams) {
        const _params = {
            ...DEFAULT_CAPTURE_THUMBNAIL_PARAMS,
            ...params,
            unique: Date.now()
        };

        try {
            const response =
                await this.rootStore.flowCXService.getPageThumbnailURL(_params);

            const thumbnail_response = await fetch(response.url);
            if (!thumbnail_response.ok) {
                const error = new Error(
                    `Error while capturing thumbnails\n${thumbnail_response.statusText}`
                );
                trackError({
                    error,
                    context: { statusCode: thumbnail_response.status }
                });
                throw error;
            }
            const contentType = thumbnail_response.headers.get('content-type');
            const arrayBuffer = await thumbnail_response.arrayBuffer();
            const base64 = arrayBufferToBase64(arrayBuffer);
            return `data:${contentType};base64,${base64}`;
        } catch (error) {
            throw error.data?.error_message || error;
        }
    }
}
